import {React, useState, useEffect} from 'react';
import { Menu, Layout, Divider, Button, Modal, message } from 'antd';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  MenuUnfoldOutlined,
  MenuFoldOutlined,
  CalendarOutlined,
  DribbbleOutlined,
  EditOutlined,
  UserOutlined,
  SwapOutlined,
  TeamOutlined,
  VideoCameraOutlined,
  BarChartOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import axios from 'axios';

const { Sider } = Layout;

const DashboardNavBar = ({ game, collapsed, onToggle }) => {
  const location = useLocation();
  const selectedKey = location.pathname;

  // Initialize state for open keys
  const [openKeys, setOpenKeys] = useState([]);

  // Dynamically update open keys based on the selected key
  useEffect(() => {
    const keys = selectedKey.includes('/player-stats')
      ? ['player-stats']
      : selectedKey.includes('/team-stats')
      ? ['team-stats']
      : [];
    setOpenKeys(keys);
  }, [selectedKey]);
  
  const navigate = useNavigate();

  // Debug: Verify the game object
  console.log('Game object:', game);

  const handleUpdateGame = () => {
    if (!game || !game.id) {
      message.error('Game ID is missing. Cannot navigate to update page.');
      return;
    }
    navigate(`/dashboard/game/${game.id}/update-game`, { state: { game } });
  };

  const handleDeleteGame = async () => {
    Modal.confirm({
      title: 'Confirm Game Deletion',
      content: (
        <div>
          <p>Are you sure you want to delete the game?</p>
          <p>
            <strong>{game.home_team}</strong> vs <strong>{game.away_team}</strong> on{' '}
            <strong>{game.date}</strong>
          </p>
          <p>This action will delete all related stats and cannot be undone.</p>
        </div>
      ),
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          const access_token = localStorage.getItem('access_token');
          await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/api/games/${game.id}/delete_game/`, {
            headers: {
              Authorization: `Bearer ${access_token}`,
            },
          });
          message.success('Game deleted successfully!');
          window.location.href = '/dashboard/games'; // Redirect to the games list
        } catch (error) {
          console.error('Error deleting game:', error);
          message.error('Failed to delete the game. Please try again.');
        }
      },
    });
  };

  return (
    <Sider
      width={250}
      collapsible
      collapsed={collapsed}
      onCollapse={onToggle} // External toggle handler
      trigger={
        <div
          style={{
            background: 'linear-gradient(135deg, #1890ff 0%, #722ed1 100%)', // Gradient background
            color: '#ffffff',
            textAlign: 'center',
            height: '100%',
            lineHeight: '40px',
          }}
        >
          {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
        </div>
      }
      style={{
        background: '#f0f0f0',
        height: '100vh',
        position: 'fixed',
        top: '64px',
        left: 0,
        zIndex: 1000,
        overflow: 'auto',
      }}
    >
      <div style={{ padding: '10px', textAlign: 'center' }}>
        {!collapsed && (
          <>
            <h4 style={{ marginBottom: 0 }}>{game.home_team}</h4>
            <p style={{ margin: 0 }}>vs.</p>
            <h4>{game.away_team}</h4>
            <div
              style={{
                marginTop: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CalendarOutlined style={{ marginRight: 8, color: '#1890ff', fontSize: '16px' }} />
              <span>{game.date}</span>
            </div>
          </>
        )}
      </div>

      {!collapsed && (
        <Divider orientation="center" plain style={{ color: '#888' }}>
          <span style={{ fontSize: '14px' }}>Analytics</span>
        </Divider>
      )}

    <Menu
      mode="inline"
      selectedKeys={[selectedKey]}
      openKeys={openKeys}
      onOpenChange={(keys) => setOpenKeys(keys)} 
        style={{ background: '#f0f0f0' }}
      >
        <Menu.Item key={`/dashboard/games`} icon={<DribbbleOutlined />}>
          <Link to={`/dashboard/games`}>My Games</Link>
        </Menu.Item>

        <Menu.Item
          key={`/dashboard/game/${game.id}/manage-data/video`}
          icon={<VideoCameraOutlined />}
        >
          <Link to={`/dashboard/game/${game.id}/manage-data/video`} state={{ game }}>
            Video Analysis
          </Link>
        </Menu.Item>

        <Menu.Item
          key={`/dashboard/game/${game.id}/manage-data/player-stats-input-ingame`}
          icon={<EditOutlined />}
        >
          <Link to={`/dashboard/game/${game.id}/manage-data/player-stats-input-ingame`} state={{ game }}>
            Player Stats Input
          </Link>
        </Menu.Item>

        <Menu.SubMenu key="player-stats" icon={<UserOutlined />} title="Player Stats">
          <Menu.Item
            key={`/dashboard/game/${game.id}/player-stats/insights`}
            icon={<BarChartOutlined />}
          >
            <Link to={`/dashboard/game/${game.id}/player-stats/insights`} state={{ game }}>
              Insights
            </Link>
          </Menu.Item>
          <Menu.Item
            key={`/dashboard/game/${game.id}/player-stats/comparison`}
            icon={<SwapOutlined />}
          >
            <Link to={`/dashboard/game/${game.id}/player-stats/comparison`} state={{ game }}>
              Comparison
            </Link>
          </Menu.Item>
        </Menu.SubMenu>

        <Menu.SubMenu key="team-stats" icon={<TeamOutlined />} title="Team Stats">
          <Menu.Item
            key={`/dashboard/game/${game.id}/team-stats/insights`}
            icon={<BarChartOutlined />}
          >
            <Link to={`/dashboard/game/${game.id}/team-stats/insights`} state={{ game }}>
              Insights
            </Link>
          </Menu.Item>
          <Menu.Item
            key={`/dashboard/game/${game.id}/team-stats/comparison`}
            icon={<SwapOutlined />}
          >
            <Link to={`/dashboard/game/${game.id}/team-stats/comparison`} state={{ game }}>
              Comparison
            </Link>
          </Menu.Item>
        </Menu.SubMenu>
      </Menu>

      {!collapsed && (
        <Divider orientation="center" plain style={{ color: '#888' }}></Divider>
      )}

{!collapsed && (
        <div
          style={{
            marginTop: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
          }}
        >
          <Button
            type="primary"
            icon={<EditOutlined />}
            style={{
              marginTop: '20px',
              width: '90%',
              backgroundColor: '#4a4a4a', // Dark grey background
              borderColor: '#4a4a4a', // Match border with background
              color: 'white', // Ensure text is visible on dark background
            }}
            onClick={handleUpdateGame}
          >
            Manage My Game
          </Button>

          <Button
            type="primary"
            danger
            icon={<DeleteOutlined />}
            style={{ marginTop: '10px', width: '90%' }}
            onClick={handleDeleteGame}
          >
            Delete My Game
          </Button>
        </div>
      )}
    </Sider>
  );
};

export default DashboardNavBar;
